import { useState, type ReactNode } from 'react';
import {
  Chip,
  HelpTip,
  Icon,
  cn,
  formatDateTime,
  groupKey,
  phaseSectionKey,
  resolveCategoryTagId,
  sectionIsClassified,
  sectionKeyForCategory,
  useCollapsedGroups,
} from '@dataroom/shared';
import type {
  CategoryGroupView,
  DdRequest,
  PhaseView,
  RoomDocument,
  SectionCertification,
} from '@dataroom/shared';
import { ChecklistItemRow } from './ChecklistItemRow';

/** Room-level certification context threaded down the phase/category tree so the
 * two-tick indicators render per section without refetching. */
export interface CertView {
  /** category name-path → `category_tag_id` (for the section key + 🔵 signal). */
  pathIndex: Map<string, string>;
  /** opaque `sectionKey` → the seller's current attestation. */
  certByKey: Map<string, SectionCertification>;
  /** Resolve an actor id to a human label (email) for the attestation record. */
  actorLabel: (actorId: string) => string;
}

/** Format an ISO timestamp as a local date-time for the attestation record. */
function formatAttestedAt(iso: string): string {
  return formatDateTime(iso, iso);
}

/**
 * The two ticks for one DD section (admin, READ-ONLY), as status chips (word +
 * icon, never emoji): Classified — the AI has filed ≥1 document into this
 * section — and Certified — the seller attested the section. Only states that
 * have happened get a chip; an untouched section shows nothing (redesign slice 4:
 * "Not certified by the seller yet" on ~100 categories was noise). A certified section
 * also shows the attestation record (who · when) since it is a legal record. The
 * admin cannot certify — no buttons here.
 */
function SectionTwoTicks({
  classified,
  certification,
  actorLabel,
  showClassified,
  showTip = false,
}: {
  classified: boolean;
  certification: SectionCertification | null;
  actorLabel: (actorId: string) => string;
  /** The phase-level/ungrouped bucket has no single category, so Classified is omitted. */
  showClassified: boolean;
  /** The "?" explaining the two chips: shown once per phase, on its first section. */
  showTip?: boolean;
}) {
  const chips: ReactNode[] = [];
  if (showClassified && classified) {
    chips.push(
      <Chip key="c" tone="classified" icon="spark">
        Classified
      </Chip>,
    );
  }
  if (certification?.needsRecertification) {
    chips.push(
      <Chip key="r" tone="recert" icon="refresh">
        Needs re-certification
      </Chip>,
    );
  } else if (certification?.status === 'complete') {
    chips.push(
      <Chip key="k" tone="certified" icon="seal">
        Certified
      </Chip>,
    );
  } else if (certification) {
    chips.push(
      <Chip key="n" tone="na" icon="notApplicable">
        Does not apply
      </Chip>,
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {chips}
      {showTip ? <HelpTip id="admin.checklist.two-ticks" /> : null}
      {certification ? (
        <span className="text-xs text-ink-3">
          {certification.status === 'complete' ? 'Certified' : 'Marked does not apply'} by{' '}
          {actorLabel(certification.certifiedBy)} · {formatAttestedAt(certification.certifiedAt)}
          {certification.note ? ` · “${certification.note}”` : ''}
        </span>
      ) : null}
    </span>
  );
}

/** A path ending in an account or state placeholder ("[Bank Account]", "[State]"):
 * answered by the roster, not by items added by hand. */
function isPlaceholderPath(path: readonly string[]): boolean {
  return /^\s*\[[^[\]]+\]\s*$/.test(path[path.length - 1] ?? '');
}

/**
 * The checklist list (redesign slice 3): each shown phase, then its categories as
 * FLAT groups — one per category, headed by its path as a breadcrumb with true
 * counts, the heading sticky while its items scroll by. Collapsing a category is
 * remembered for the room in this browser.
 */
export function ChecklistList({
  roomId,
  views,
  documents,
  certView,
  emptyText,
  adding,
  onStartAdd,
  renderAddRow,
  highlightId,
  editingId,
  onStartEdit,
  renderEditRow,
  onReviewDocument,
}: {
  roomId: string;
  views: PhaseView[];
  documents: RoomDocument[];
  certView: CertView;
  /** Said when nothing is shown (the filter, phase or search matches nothing). */
  emptyText: string;
  /** The category whose "+ Add item" is open ({@link groupKey}), or null. */
  adding: string | null;
  onStartAdd: (phaseId: string, path: string[]) => void;
  /** The add-item row for the open category. */
  renderAddRow: () => ReactNode;
  /** The item just added (brought into view and marked). */
  highlightId: string | null;
  /** The item whose edit form is open, its opener and the form itself. */
  editingId: string | null;
  onStartEdit: (req: DdRequest) => void;
  renderEditRow: (req: DdRequest) => ReactNode;
  /** Open a document's classification review (offered after an AI match's "Not this one"). */
  onReviewDocument?: (documentId: string) => void;
}) {
  const collapsed = useCollapsedGroups(roomId);
  if (views.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {emptyText}
      </p>
    );
  }
  return (
    <div className="space-y-4">
      {views.map((view, i) => (
        <PhaseSection
          key={view.key}
          roomId={roomId}
          view={view}
          documents={documents}
          certView={certView}
          collapsed={collapsed}
          // The two-ticks "?" sits on the first section of the list, once.
          ticksTip={i === 0}
          adding={adding}
          onStartAdd={onStartAdd}
          renderAddRow={renderAddRow}
          highlightId={highlightId}
          editingId={editingId}
          onStartEdit={onStartEdit}
          renderEditRow={renderEditRow}
          onReviewDocument={onReviewDocument}
        />
      ))}
    </div>
  );
}

/** One phase: a collapsible header with its counts (its status and dates live in
 * the phase plan), then its ungrouped items and its categories, each category with
 * "+ Add item" at its foot. */
function PhaseSection({
  roomId,
  view,
  documents,
  certView,
  collapsed,
  ticksTip,
  adding,
  onStartAdd,
  renderAddRow,
  highlightId,
  editingId,
  onStartEdit,
  renderEditRow,
  onReviewDocument,
}: {
  roomId: string;
  view: PhaseView;
  documents: RoomDocument[];
  certView: CertView;
  collapsed: ReturnType<typeof useCollapsedGroups>;
  ticksTip: boolean;
  adding: string | null;
  onStartAdd: (phaseId: string, path: string[]) => void;
  renderAddRow: () => ReactNode;
  highlightId: string | null;
  editingId: string | null;
  onStartEdit: (req: DdRequest) => void;
  renderEditRow: (req: DdRequest) => ReactNode;
  onReviewDocument?: (documentId: string) => void;
}) {
  const { phase } = view;
  const legacy = phase.phaseId === null;
  const [open, setOpen] = useState(true);
  const shownItems = [...view.uncategorized, ...view.groups.flatMap((g) => g.items)];
  // The "?" on the status chip sits on the first row of each phase, not on every row.
  const firstStatusId = shownItems[0]?.id;
  const renderItem = (req: DdRequest): ReactNode => (
    <ChecklistItemRow
      key={req.id}
      roomId={roomId}
      req={req}
      documents={documents}
      actorLabel={certView.actorLabel}
      statusTip={req.id === firstStatusId}
      onReviewDocument={onReviewDocument}
      highlighted={req.id === highlightId}
      onEdit={() => onStartEdit(req)}
      editForm={req.id === editingId ? renderEditRow(req) : undefined}
    />
  );
  // The ungrouped (no-category) items form a phase-level certification section.
  const ungroupedCert = phase.phaseId
    ? (certView.certByKey.get(phaseSectionKey(phase.phaseId)) ?? null)
    : null;
  const resolved = phase.covered + phase.notApplicable;
  const bodyId = `phase-${view.key}`;

  return (
    <section className="rounded-lg border" aria-labelledby={`${bodyId}-h`}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 p-4">
        <h3 id={`${bodyId}-h`} className="scroll-mt-24 text-base font-semibold">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1.5 rounded-md text-left hover:underline"
          >
            <Icon
              name="chevronRight"
              className={cn('text-ink-3 transition-transform', open && 'rotate-90')}
            />
            {phase.name ?? 'Items not in a phase'}
          </button>
        </h3>
        <span className="text-xs text-ink-2">
          {resolved} of {phase.total} resolved
          {phase.missing > 0 ? ` · ${phase.missing} missing` : ''}
          {phase.waitingOnReview > 0 ? ` · ${phase.waitingOnReview} waiting on review` : ''}
          {phase.notApplicable > 0 ? ` · ${phase.notApplicable} not applicable` : ''}
        </span>
        {legacy ? (
          <p className="basis-full text-xs text-ink-2">
            Added before this room used phases. Move one into a phase with its ⋯ menu → Edit, or
            leave them here — they still count toward the checklist.
          </p>
        ) : null}
      </div>

      {open ? (
        <div id={bodyId} className="space-y-3 border-t px-4 pb-4 pt-3">
          {phase.total === 0 ? (
            <p className="text-sm text-muted-foreground">
              No items in this phase yet. Use Add item above the list to add one.
            </p>
          ) : (
            <>
              {view.uncategorized.length > 0 ? (
                <div className="space-y-2">
                  {phase.phaseId ? (
                    <SectionTwoTicks
                      classified={false}
                      showClassified={false}
                      certification={ungroupedCert}
                      actorLabel={certView.actorLabel}
                      showTip={ticksTip && view.groups.length === 0}
                    />
                  ) : null}
                  <ul className="divide-y rounded-md border">
                    {view.uncategorized.map(renderItem)}
                  </ul>
                </div>
              ) : null}
              {view.groups.map((group, i) => (
                <CategorySection
                  key={group.id}
                  group={group}
                  phaseId={phase.phaseId}
                  documents={documents}
                  certView={certView}
                  renderItem={renderItem}
                  showTip={ticksTip && i === 0}
                  collapsed={collapsed.isCollapsed(groupKey(view.key, group.path))}
                  onToggle={() => collapsed.toggle(groupKey(view.key, group.path))}
                  addOpen={adding === groupKey(view.key, group.path)}
                  onStartAdd={
                    phase.phaseId && !isPlaceholderPath(group.path)
                      ? () => onStartAdd(phase.phaseId!, group.path)
                      : undefined
                  }
                  renderAddRow={renderAddRow}
                />
              ))}
            </>
          )}

        </div>
      ) : null}
    </section>
  );
}

/** One category: its path as a breadcrumb, its true counts and two ticks — sticky
 * while its items scroll by — then its items. */
function CategorySection({
  group,
  phaseId,
  documents,
  certView,
  renderItem,
  showTip,
  collapsed,
  onToggle,
  addOpen,
  onStartAdd,
  renderAddRow,
}: {
  group: CategoryGroupView;
  phaseId: string | null;
  documents: RoomDocument[];
  certView: CertView;
  renderItem: (req: DdRequest) => ReactNode;
  showTip: boolean;
  collapsed: boolean;
  onToggle: () => void;
  /** This category's add-item row is open. */
  addOpen: boolean;
  /** Open it (absent where items are not added by hand: account/state placeholders). */
  onStartAdd?: () => void;
  renderAddRow: () => ReactNode;
}) {
  // A section = a category (holding requested items) within a phase.
  const tagId = group.all[0]?.categoryTagId ?? null;
  const sectionTagId = tagId ?? resolveCategoryTagId(certView.pathIndex, group.path);
  const sectionKey = phaseId ? sectionKeyForCategory(phaseId, tagId) : null;
  const certification = sectionKey ? (certView.certByKey.get(sectionKey) ?? null) : null;
  const classified = phaseId ? sectionIsClassified(documents, sectionTagId, group.path) : false;
  const listId = `${group.id}-items`;

  return (
    <div className="space-y-2">
      <div className="-mx-1 flex flex-wrap items-center gap-x-3 gap-y-1 bg-card px-1 py-1 md:sticky md:top-[var(--header-h)] md:z-10">
        <h4 id={group.id} className="scroll-mt-24 text-sm font-semibold">
          <button
            type="button"
            aria-expanded={!collapsed}
            aria-controls={listId}
            onClick={onToggle}
            className="inline-flex items-center gap-1.5 rounded-md text-left hover:underline"
          >
            <Icon
              name="chevronRight"
              className={cn('shrink-0 text-ink-3 transition-transform', !collapsed && 'rotate-90')}
            />
            {group.heading}
          </button>
        </h4>
        <span className="text-xs text-ink-3">
          {group.total} {group.total === 1 ? 'item' : 'items'}
          {group.missing > 0 ? ` · ${group.missing} missing` : ''}
        </span>
        {phaseId ? (
          <SectionTwoTicks
            classified={classified}
            showClassified
            certification={certification}
            actorLabel={certView.actorLabel}
            showTip={showTip}
          />
        ) : null}
      </div>
      {!collapsed ? (
        <>
          {group.items.length > 0 ? (
            <ul id={listId} className="divide-y rounded-md border">
              {group.items.map(renderItem)}
            </ul>
          ) : null}
          {addOpen ? (
            renderAddRow()
          ) : onStartAdd ? (
            <button
              type="button"
              onClick={onStartAdd}
              className="inline-flex items-center gap-1 rounded px-1 text-xs font-medium text-primary hover:underline max-md:min-h-tap"
            >
              <Icon name="plus" size={12} />
              Add item
              <span className="sr-only"> to {group.heading}</span>
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
